"use client";

import React, { type ReactNode } from "react";
import Image from "next/image";

interface ErrorDisplayProps {
  error: Error & { digest?: string };
  /** When set, replaces the default title and body copy below the icon. */
  content?: ReactNode;
  /** Stable selector for QA automation. */
  testId?: string;
}

const errorIcon = (
  <div
    data-testid="error-display-icon"
    className="w-24 h-24 rounded-full flex items-center justify-center"
  >
    <Image
      src="/icons/cross.png"
      alt="Error"
      width={48}
      height={48}
      className="w-18 h-18"
    />
  </div>
);

const defaultMessage = (
  <>
    <h2
      data-testid="error-display-title"
      className="text-2xl font-bold mt-5 text-ng-font-1"
    >
      エラー
    </h2>
    <p
      data-testid="error-display-description"
      className="text-base text-ng-font-1 mt-3 text-center"
    >
      正常に処理できませんでした。
    </p>
    <p
      data-testid="error-display-retry-hint"
      className="text-base text-ng-font-1 mt-3 text-center"
    >
      再度お試しください。
    </p>
  </>
);

const ErrorDisplay: React.FC<ErrorDisplayProps> = ({
  error,
  content,
  testId = "error-display",
}) => {
  return (
    <div
      data-testid={testId}
      className={`min-h-screen flex flex-col items-center justify-center p-4 bg-ng-background`}
    >
      {errorIcon}
      {content ?? defaultMessage}

      {/* Optional: Show error details if needed (can be hidden in production) */}
      {process.env.APP_ENV === "development" && error.message && (
        <p
          data-testid="error-display-detail"
          className="text-sm text-gray-500 mt-12 text-center max-w-md"
        >
          {error.message}
        </p>
      )}
    </div>
  );
};

export default ErrorDisplay;
